// =============================================================================
// SLIDESHOW-HORIZON — Horizon `Component` that WRAPS the vendored classic Swiper engine.
// HORIZON PILOT #2: the Motto slideshow's carousel behaviour reparented onto Horizon's `Component`
// base class (import { Component } from '@theme/component', resolved via the head importmap in
// snippets/horizon-importmap.liquid), loaded as native ESM through a section-scoped
// `<script type="module">` in sections/slideshow-horizon.liquid — NOT the webpack src/js bundle.
//
// This element IS the `.slideshow` root (`<slideshow-horizon class="slideshow slideshow-horizon …">`).
// It reads the section config from its own `data-*` attributes (the SAME attrs the vendored
// assets/slideshow.js reads off `.slideshow`) and reparents the vendored `slideshow()` function's
// Swiper init + slide-change side-effects onto the Component lifecycle:
//   • connectedCallback  → wait for `window.Swiper` to be ready, then `new window.Swiper(this.refs.swiper, …)`
//                          (double-init guarded). Declarative `ref=` locates the container + pagination.
//   • disconnectedCallback → `this.#swiper.destroy()` + tear down the ResizeObserver + hover listeners.
//
// WHY a Component-wrapped Swiper (not native scroll-snap): this pilot establishes the pattern for
// porting an EXISTING Swiper-driven Motto section to Horizon without rewriting the engine — the
// carousel stays byte-for-byte Swiper, only its ownership/lifecycle moves onto `Component`.
//
// INDEPENDENCE from the vendored assets/slideshow.js (which stays for the ORIGINAL slideshow):
//   • This section's schema `class` is `slideshow-horizon-section` (NOT `slideshow-section`), so the
//     vendored `$('.slideshow-section')` init never touches it — no double Swiper on one container.
//   • The split-type HEADING text animation (vendored `headingAnimation()`, a GLOBAL non-scoped
//     concern loaded conditionally by `text_animation`) is intentionally OUT of pilot scope, so the
//     horizon section omits `text_animation` and emits no `.js-split-text`. Headings render statically
//     (identical to the original with the toggle OFF). The Component owns ONLY the Swiper carousel.
//
// `type="module"` scripts DEFER and run AFTER Motto's classic head scripts. `swiper-bundle.min.js` is a
// classic `defer` script in the head, so it executes BEFORE this body module in document order —
// `window.Swiper` is normally ready by the time we init. The readiness poll below is defence-in-depth
// (theme-editor re-injection, load-order drift).
//
// UNTRACKED new file (alongside assets/component.js + assets/utilities.js + media-with-content-note.js) —
// re-verify it exists before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';

/**
 * The slideshow custom element. It is the `.slideshow` root; `this.refs.swiper` is the `.swiper`
 * container Swiper mounts on, `this.refs.pagination` the bullets container (absent in the hero layout).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} swiper - the `.slideshow__swiper.swiper` Swiper mounts on.
 * @property {HTMLElement} [pagination] - the `.swiper-pagination` bullets host (multi-slide layouts only).
 *
 * @extends {Component<Refs>}
 */
export class SlideshowHorizon extends Component {
  /** @type {import('@theme/component') | any} */
  #swiper = null;
  #resizeObserver = null;
  #controller = null;
  #initialised = false;

  connectedCallback() {
    super.connectedCallback(); // populates this.refs from [ref] descendants
    if (this.#initialised) return;
    this.#whenSwiperReady(() => this.#init());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Swiper `destroy(deleteInstance, cleanStyles)` removes its own listeners + `el.swiper`.
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#controller?.abort(); // tears down the custom-effect hover-pause listeners
    this.#controller = null;
    this.#initialised = false;
  }

  /**
   * `window.Swiper` is the vendored classic global loaded via a `defer` head script. It is normally
   * present by the time this deferred module runs; poll briefly (≈5s cap) as a guard for re-injection
   * / load-order drift, then give up silently (the server-rendered slides stay visible either way).
   */
  #whenSwiperReady(callback) {
    if (window.Swiper) {
      callback();
      return;
    }
    let tries = 0;
    const timer = setInterval(() => {
      if (window.Swiper) {
        clearInterval(timer);
        callback();
      } else if (++tries > 100) {
        clearInterval(timer);
      }
    }, 50);
  }

  #init() {
    if (this.#initialised || !this.isConnected) return;
    const swiperEl = this.refs.swiper;
    // Double-init guard: Swiper attaches its instance to the element as `el.swiper`.
    if (!swiperEl || swiperEl.swiper) return;
    this.#initialised = true;

    const host = this; // the `.slideshow` root — carries the config + the elements the effects touch
    const ds = this.dataset;
    const autoplayOn = ds.autoplay === 'true';
    const stopAutoplay = ds.stopAutoplay === 'true';
    const delay = (parseFloat(ds.delay) || 3) * 1000;
    const speed = (parseFloat(ds.speed) || 0.8) * 1000;
    const rawEffect = ds.effect || 'custom';
    // Mirrors the vendored map: the merchant "custom" effect drives Swiper's `fade`. NB — as in the
    // vendored code, `effect` is already remapped here, so `virtualTranslate: effect === 'custom'`
    // resolves false; preserved verbatim to keep behaviour identical to the original.
    const effect = rawEffect === 'custom' ? 'fade' : rawEffect;

    const params = {
      parallax: ds.parallax === 'true',
      effect,
      virtualTranslate: effect === 'custom',
      watchSlidesProgress: true,
      speed,
      loop: false,
      centeredSlides: false,
      autoHeight: false,
      keyboard: true,
      fadeEffect: { crossFade: true },
      creativeEffect: {
        prev: { shadow: false, translate: [0, 0, -400] },
        next: { translate: ['100%', 0, 0] },
      },
      coverflowEffect: { rotate: 50, stretch: 0, depth: 100, modifier: 1, slideShadows: false },
      pagination: this.refs.pagination
        ? { el: this.refs.pagination, clickable: true, type: 'bullets' }
        : false,
      ...(autoplayOn
        ? {
            autoplay: {
              delay,
              pauseOnMouseEnter: stopAutoplay,
              disableOnInteraction: false,
              waitForTransition: true,
            },
          }
        : {}),
      on: {
        init() {
          // Fix for first slide (custom/fade): mark the already-active first slide so its intro
          // animation is suppressed on load.
          if (rawEffect === 'custom') {
            const firstSlide = this.slides[0];
            if (firstSlide?.classList.contains('swiper-slide-active')) {
              firstSlide.classList.add('no-animation');
            }
          }
        },
      },
    };

    const swiper = new window.Swiper(swiperEl, params);
    this.#swiper = swiper;

    // z-index ladder for the custom (stacked-fade) effect — starts at the slide count and climbs so
    // the incoming slide always paints above the outgoing one.
    let zIndex = host.querySelectorAll('.slideshow-swiper__slide').length;

    // Custom-effect hover pause: the vendored engine adds explicit enter/leave listeners on the
    // wrapper (Swiper's own `pauseOnMouseEnter` is unreliable with the virtual-translate stack).
    if (rawEffect === 'custom' && autoplayOn && stopAutoplay) {
      this.#controller = new AbortController();
      const { signal } = this.#controller;
      const wrapper = swiper.el.querySelector('.swiper-wrapper');
      wrapper?.addEventListener('mouseenter', () => swiper.autoplay.stop(), { signal });
      wrapper?.addEventListener('mouseleave', () => swiper.autoplay.start(), { signal });
    }

    swiper.on('transitionStart', function () {
      if (rawEffect === 'custom') {
        zIndex += 1;
        this.slides[this.activeIndex]?.style.setProperty('z-index', `${zIndex}`);
      }
    });

    swiper.on('beforeTransitionStart', function () {
      applyColorScheme(host, this);
    });

    swiper.on('slideChange', function () {
      host.style.setProperty('--bullet-duration', `${delay + speed}ms`);
      applyColorScheme(host, this);

      if (rawEffect === 'custom') {
        // Fix for first slide — re-enable its intro once the carousel has moved.
        this.slides[0]?.classList.remove('no-animation');

        // Fix autoplay cadence for the custom effect (account for the transition time once).
        if (this.params.autoplay && this.params.autoplay.waitForTransition === true) {
          this.params.autoplay.delay = delay + speed;
          this.params.autoplay.waitForTransition = false;
          this.autoplay.stop();
          this.autoplay.start();
        }
      }

      // Restart any video in the newly-active slide from the top.
      const activeSlide = this.slides[this.activeIndex];
      const video = activeSlide?.querySelector('video');
      if (video) {
        video.currentTime = 0;
        video.play();
      }
    });

    // Publish the usable slide width (container inner width) as `--width-slide`, as the vendored code
    // does, so the slide layout can size against it.
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const container = entry.target.querySelector('.container');
        if (!container) return;
        const style = window.getComputedStyle(container);
        const paddingLeft = parseFloat(style.paddingLeft) || 0;
        const paddingRight = parseFloat(style.paddingRight) || 0;
        const widthSlide = entry.contentRect.width - paddingLeft - paddingRight;
        entry.target.style.setProperty('--width-slide', `${widthSlide}px`);
      });
    });
    resizeObserver.observe(host);
    this.#resizeObserver = resizeObserver;
  }
}

/**
 * Recolour the pagination bar (`.slideshow-bottom`) to match the active slide's `color-*` scheme —
 * a vanilla port of the vendored `colorScheme()`. No-op in the hero layout (no `.slideshow-bottom`).
 *
 * @param {HTMLElement} host - the `.slideshow` root.
 * @param {any} swiper - the Swiper instance (`swiper.slides`, `swiper.activeIndex`).
 */
function applyColorScheme(host, swiper) {
  const bar = host.querySelector('.slideshow-bottom');
  if (!bar) return;
  const activeSlide = swiper.slides[swiper.activeIndex];
  const scheme = activeSlide?.querySelector('.slideshow-slide')?.dataset.colorScheme;
  if (!scheme) return;
  for (const className of [...bar.classList]) {
    if (/color-background-\d+$/.test(className)) bar.classList.remove(className);
  }
  bar.classList.add(scheme);
}

if (!customElements.get('slideshow-horizon')) {
  customElements.define('slideshow-horizon', SlideshowHorizon);
}
